import isEqual from 'fast-deep-equal';

import { isBrowser, isDefined, isNotDefined } from '@enterprise/core';

import type { IntersectionCallback } from './types/intersection-callback.type';
import type { IntersectionData } from './types/intersection-data.type';
import type { IntersectionObserverSingleton } from './types/intersection-observer.singleton';
import type { IntersectionOptions } from './types/intersection.options';

/**
 * Returns a singleton instance of an IntersectionObserver with custom callback management.
 *
 * This utility ensures that only one IntersectionObserver instance is created and reused across the application.
 * It manages callbacks for each observed element and tracks intersection data such as visibility, count, and state.
 * The observer is only created in a browser environment.
 *
 * @param options - Optional configuration for the IntersectionObserver and observer behavior.
 *   - `once`: If true, the callback will only be triggered once per element when it first becomes visible.
 *   - Other options are passed directly to the IntersectionObserver constructor.
 * @returns The singleton IntersectionObserver instance, or `undefined` if not running in a browser.
 *
 * @remarks
 * - Uses `globalThis.enterpriseIntersectionObserver` to store the singleton instance.
 * - Tracks intersection data for each observed element, including visibility state and count.
 * - Provides `add` and `remove` methods to manage callbacks for each target element.
 */
export const getIntersectionObserver = (options?: IntersectionOptions): IntersectionObserverSingleton | undefined => {
  if (!isBrowser) {
    return undefined;
  }

  const defaultOptions = { once: false, threshold: 0 };
  const { once, ...observerOptions } = { ...defaultOptions, ...options };

  const existing = globalThis.enterpriseIntersectionObserver;

  if (isDefined(existing) && isDefined(existing.options)) {
    const previousOptions = existing.options;
    const changed = !isEqual(previousOptions, options);

    if (!changed) {
      return existing;
    }

    existing.observer.disconnect();
  }

  const callbacks = new Map<Element, Set<IntersectionCallback>>();
  const intersectionData = new Map<Element, IntersectionData>();
  const observer = new IntersectionObserver((entries) => {
    requestAnimationFrame(() => {
      for (const { isIntersecting, target } of entries) {
        const intersectionCallbacks = callbacks.get(target);

        if (isDefined(intersectionCallbacks)) {
          let data = intersectionData.get(target);

          if (isNotDefined(data)) {
            data = {
              count: 0,
              hasBeenVisible: false,
              isVisible: false,
            };
          }

          const count = data.count;

          if (isIntersecting && count > 0 && once) {
            return;
          }

          data.count = isIntersecting ? count + 1 : count;
          data.hasBeenVisible = count > 0;
          data.isVisible = isIntersecting;

          intersectionData.set(target, data);

          for (const callback of intersectionCallbacks) {
            callback(data);
          }
        }
      }
    });
  }, observerOptions);

  const intersectionObserver: IntersectionObserverSingleton = {
    add: (target, callback) => {
      let intersectionCallbacks = callbacks.get(target);

      if (isNotDefined(intersectionCallbacks)) {
        intersectionCallbacks = new Set<IntersectionCallback>();
        callbacks.set(target, intersectionCallbacks);
        observer.observe(target);
      }

      intersectionCallbacks.add(callback);
    },
    observer,
    options: { once, ...observerOptions },
    remove: (target, callback) => {
      const intersectionCallbacks = callbacks.get(target);

      if (isDefined(intersectionCallbacks)) {
        intersectionCallbacks.delete(callback);

        if (intersectionCallbacks.size === 0) {
          callbacks.delete(target);
          observer.unobserve(target);
        }
      }
    },
  };

  globalThis.enterpriseIntersectionObserver = intersectionObserver;

  return intersectionObserver;
};
